import styled from '@emotion/styled';
import type { ObjectId } from '@racemap/sdk/schema/base';
import { MaxMobileWindowWidth, RacemapColors } from '@racemap/utilities/consts/common';
import { range } from '@racemap/utilities/functions/utils';
import type { Immutable } from 'immer';
import React, { type CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import useScrollbarSize from 'react-scrollbar-size';
import {
  AutoSizer,
  type RowMouseEventHandlerParams,
  SortDirection,
  type SortDirectionType,
  Table,
  type TableProps,
  type TableRowProps,
} from 'react-virtualized';
import { defaultRowRenderer } from 'react-virtualized/dist/es/Table';
import 'react-virtualized/styles.css';
import { useWindowSize } from 'usehooks-ts';
import type { TrackerColumnKeys } from '../../store/trackers/trackers_reducers';
import { CheckboxValues } from '../BasicComponents/Checkbox';
import { Preview } from '../BasicComponents/DragLayer';
import { DragLayer } from '../BasicComponents/DragLayer/DragLayer';
import { SearchBar } from '../BasicComponents/SearchBar/SearchBar';
import { DefaultNoDataPlaceholder } from './DefaultNoDataPlaceholder';
import { DraggableRow } from './DraggableRow';
import {
  type BasicColumnProps as ColumnProps,
  checkboxColumn,
  placeholderColumn,
  rowIndexColumn,
} from './columns';
import {
  getFilteredData,
  getMinWidth,
  getSortedData,
  getSortingMethods,
  getUsefulWidth,
} from './helpers';

export const HEADER_HEIGHT = 30;
export const ROW_HEIGHT = 30;

// **************************************
// Styles
// **************************************

const defaultHeaderStyle: CSSProperties = {
  height: HEADER_HEIGHT,
  borderBottom: 'solid 1px #dee2e6',
  alignItems: 'stretch',
};

const defaultRowStyle: CSSProperties = {
  height: ROW_HEIGHT,
  borderBottom: 'solid 1px #dee2e6',
  alignItems: 'stretch',
  outline: 'none',
};

export const defaultCellStyle: CSSProperties = {
  margin: 'unset',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
};

// **************************************
// Component
// **************************************

interface Props<T, I extends ObjectId | string> extends Partial<TableProps> {
  children: Array<React.ReactElement<ColumnProps>>;
  data: Immutable<Array<T>>;
  customDefaultVisibleColumns?: Array<string>;
  defaultSortingKey?: string;
  defaultSortDirection?: SortDirectionType;
  withRowIndex?: boolean;
  noDataPlaceholder?: React.ReactNode;
  stripped?: boolean;
  hover?: boolean;
  withSearchBar?: boolean;
  className?: string;
  style?: CSSProperties;
  selectionIdKey?: keyof Immutable<T>;
  selectionIds?: Immutable<Set<string>>;
  onSelectionChange?: (newSelectionIds: Immutable<Set<string>>) => void;
  draggable?: boolean;
  dragElementType?: string;
  disabledRowIds?: Immutable<Set<string>>;
  focusedRowId?: I | null;
  onFocusRowChange?: (newFocusId: Immutable<I> | null) => void;
  withKeyControl?: boolean;
  overscanRowCount?: number;
  onSortChange?: (sortBy: string | undefined, sortDirection: SortDirectionType) => void;
  getRowStyle?: (
    basicRowStyle: CSSProperties,
    rowData: Immutable<T>,
    isFocused: boolean,
    isDisabled: boolean,
    isSelected: boolean,
  ) => CSSProperties;
  columnsCanBeHidden?: boolean;
  changeSelectedTrackerCols?: (selectedCols: Array<TrackerColumnKeys>) => void;
  isSortingCheckboxCol?: boolean;
}

export interface Entry<I extends ObjectId | string> extends Record<string, unknown> {
  id: I;
}

export const DataTable = <T extends Entry<I>, I extends ObjectId | string = string>({
  data,
  children,
  defaultSortingKey,
  defaultSortDirection,
  headerStyle = {},
  rowStyle = {},
  tableStyle = {},
  withRowIndex,
  stripped = false,
  hover = false,
  withSearchBar = false,
  noDataPlaceholder = <DefaultNoDataPlaceholder />,
  className,
  style,
  selectionIds,
  onSelectionChange,
  selectionIdKey = 'id',
  draggable = false,
  dragElementType = 'datatable-row',
  disabledRowIds,
  focusedRowId,
  onFocusRowChange,
  withKeyControl = true,
  getRowStyle,
  onSortChange,
  overscanRowCount = 10,
  columnsCanBeHidden = false,
  isSortingCheckboxCol = false,
}: Props<T, I>): React.ReactElement => {
  const combinedHeaderStyle = { ...defaultHeaderStyle, ...headerStyle };
  const combinedRowStyle = { ...defaultRowStyle, ...rowStyle };
  const [sortBy, setSortBy] = useState<string | undefined>(defaultSortingKey);
  const [searchValue, setSearchValue] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(
    defaultSortDirection || SortDirection.ASC,
  );
  const { width: windowWidth } = useWindowSize();
  const { width: scrollBarWidth } = useScrollbarSize();
  const [scrollToIndex, setScrollToIndex] = useState<number | undefined>(undefined);
  const [shiftKeyPushed, setShiftKeyPushed] = useState(false);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | undefined>(undefined);
  const selectionSortingMethod = (_cA: boolean, _cB: boolean, rowDataA: T, rowDataB: T): number => {
    if (!selectionIds) return 0;

    const isASelected = selectionIds.has(rowDataA.id.toString());
    const isBSelected = selectionIds.has(rowDataB.id.toString());

    if (isASelected && !isBSelected) return -1;
    if (!isASelected && isBSelected) return 1;

    return 0;
  };
  const sortingMethods = useMemo(
    () => getSortingMethods(children, selectionSortingMethod),
    [children],
  );
  const sortedData = useMemo(
    () => getSortedData(data, sortBy, sortDirection, sortingMethods),
    [data, sortBy, sortDirection, sortingMethods],
  );
  const filteredData = useMemo(
    () => getFilteredData(sortedData, searchValue, children),
    [sortedData, searchValue, children],
  );
  const minWidth = getMinWidth(children);
  const usefulWidth = getUsefulWidth(children, !!withRowIndex);
  const isOverflowing = minWidth > windowWidth - 30;
  const existsSearchableColumns = children.find((c) => !!c.props.columnData?.searchable);
  const selectable = selectionIds != null && onSelectionChange != null;
  const focusable = onFocusRowChange != null;

  // auto scroll to focused row
  useEffect(() => {
    const index = filteredData.findIndex((fD) => isEqualId(fD.id, focusedRowId));
    if (focusedRowId == null || index === -1) {
      setScrollToIndex(undefined);
    } else {
      setScrollToIndex(index);
    }
  }, [focusedRowId]);

  // catch shift key press global
  useEffect(() => {
    const isSupported = document?.addEventListener;
    if (!isSupported) return;

    const handleKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Shift') setShiftKeyPushed(true);
    };
    const handleKeyUp = (ev: KeyboardEvent) => {
      if (ev.key === 'Shift') setShiftKeyPushed(false);
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
    };
  }, []);

  function getFinalRowStyle({ index }: { index: number }) {
    if (index === -1) return combinedHeaderStyle;
    const rowStyle = { ...combinedRowStyle };
    const rowData = filteredData[index];
    if (rowData == null) return rowStyle;

    const isSelected = !!selectionIds?.has(rowData.id.toString());
    const isFocused = isEqualId(rowData?.id, focusedRowId);
    const isDisabled = !!disabledRowIds?.has(rowData.id.toString());

    if (onFocusRowChange != null) {
      rowStyle.cursor = 'pointer';
    }

    if (isSelected) {
      rowStyle.background = RacemapColors.CloudBlue;
      if (draggable) rowStyle.cursor = 'grab';
    }

    if (draggable && !selectable && !focusable) {
      rowStyle.cursor = 'grab';
    }

    if (isDisabled) {
      rowStyle.opacity = 0.5;
      rowStyle.cursor = 'not-allowed';
    }

    if (isFocused) {
      rowStyle.boxShadow = `inset -1px 0px 1px 3px ${RacemapColors.PaleBlue}`;
    }

    return getRowStyle?.(rowStyle, rowData, isFocused, isDisabled, isSelected) || rowStyle;
  }

  const getRowRenderer = useCallback(
    (props: TableRowProps) => {
      if (!draggable) return defaultRowRenderer(props);
      const id = props.rowData.id || props.key;

      return (
        <DraggableRow
          key={props.key}
          rowId={id.toString()}
          selectionIds={selectionIds}
          itemType={dragElementType}
          // style prop is workaround to avaoid warning
          style={{}}
        >
          {defaultRowRenderer(props)}
        </DraggableRow>
      );
    },
    [draggable, selectionIds, dragElementType],
  );

  const handleSortingChange = useCallback(
    ({
      sortBy: newSortBy,
      sortDirection: newSortDirection,
    }: {
      sortBy: string;
      sortDirection: SortDirectionType;
    }) => {
      const dfSortDir = defaultSortDirection || SortDirection.ASC;
      if (newSortBy === sortBy && newSortDirection === dfSortDir) {
        setSortDirection(dfSortDir);
        setSortBy(undefined);
        onSortChange?.(undefined, dfSortDir);
        return;
      }
      setSortBy(newSortBy);
      setSortDirection(newSortDirection);
      onSortChange?.(newSortBy, newSortDirection);
    },
    [setSortBy, sortBy, setSortDirection, defaultSortDirection],
  );

  const getRowSelectionState = useCallback(
    ({ index }: { index: number }): CheckboxValues => {
      if (!selectable) return CheckboxValues.NOT_CHECKED;
      if (index === -1) {
        if (selectionIds.size > 0) {
          if (selectionIds.size === filteredData.length) return CheckboxValues.CHECKED;
          return CheckboxValues.PARTIAL_CHECKED;
        }

        return CheckboxValues.NOT_CHECKED;
      }
      const data = filteredData[index];
      if (data == null) return CheckboxValues.NOT_CHECKED;
      if (!selectionIds?.has(data.id.toString())) return CheckboxValues.NOT_CHECKED;

      return CheckboxValues.CHECKED;
    },
    [selectable, selectionIds, filteredData],
  );

  function handleSelectionChange({ index, newValue }: { index: number; newValue: boolean }) {
    if (!selectable) return;
    if (index === -1) {
      // handle the header click on selection field
      if (newValue) {
        onSelectionChange(new Set(filteredData.map((fD) => fD.id.toString())));
      } else {
        onSelectionChange(new Set());
      }
      setLastSelectedIndex(undefined);
      return;
    }
    const indexes = [];
    if (shiftKeyPushed && newValue && lastSelectedIndex != null) {
      indexes.push(...range(lastSelectedIndex, index));
    } else {
      indexes.push(index);
    }

    const newSelectionIds = new Set(selectionIds);
    for (const i of indexes) {
      const data = filteredData[i];
      if (data == null) continue;
      const idValue = data[selectionIdKey]?.toString();
      if (idValue == null || disabledRowIds?.has(idValue)) continue;

      if (newValue) {
        newSelectionIds.add(idValue);
      } else {
        newSelectionIds.delete(idValue);
      }
    }

    if (newValue) {
      setLastSelectedIndex(indexes[indexes.length - 1]);
    } else {
      setLastSelectedIndex(undefined);
    }

    onSelectionChange(newSelectionIds);
  }

  function handleRowClick({
    index,
  }: RowMouseEventHandlerParams & { event: React.MouseEvent<HTMLDivElement, MouseEvent> }) {
    const data = filteredData[index];
    if (data == null) return;
    const idValue = data[selectionIdKey] as Immutable<I> | null;
    if (idValue == null) return;

    onFocusRowChange?.(isEqualId(idValue, focusedRowId) ? null : idValue);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (!withKeyControl) return;
    // only react on valid keys
    if (!['ArrowUp', 'ArrowDown', ' ', 'Shift'].includes(event.key)) return;
    // only react if focus is set
    if (focusedRowId == null || onFocusRowChange == null) return;

    event.stopPropagation();
    event.preventDefault();

    const focusIndex = filteredData.findIndex((fD) => isEqualId(fD.id, focusedRowId));
    if (focusIndex < 0) return;

    // move the focus 1 up
    if (event.key === 'ArrowUp' && focusIndex > 0) {
      const newFocusRow = filteredData[focusIndex - 1];
      if (newFocusRow == null) return;

      if (shiftKeyPushed && onSelectionChange != null) {
        const newSelection = new Set(selectionIds);
        if (!disabledRowIds?.has(focusedRowId.toString()))
          newSelection.add(focusedRowId.toString());
        if (!disabledRowIds?.has(newFocusRow.id.toString()))
          newSelection.add(newFocusRow.id.toString());
        onSelectionChange(newSelection);
      }

      onFocusRowChange(newFocusRow.id);
      setScrollToIndex(focusIndex - 1);
      return;
    }

    // move the focus 1 down
    if (event.key === 'ArrowDown' && focusIndex < filteredData.length) {
      const newFocusRow = filteredData[focusIndex + 1];
      if (newFocusRow == null) return;

      if (shiftKeyPushed && onSelectionChange != null) {
        const newSelection = new Set(selectionIds);
        if (!disabledRowIds?.has(focusedRowId.toString()))
          newSelection.add(focusedRowId.toString());
        if (!disabledRowIds?.has(newFocusRow.id.toString()))
          newSelection.add(newFocusRow.id.toString());
        onSelectionChange(newSelection);
      }

      onFocusRowChange(newFocusRow.id);
      setScrollToIndex(focusIndex + 1);
      return;
    }

    // switch the selection state
    if (event.key === ' ' && selectionIds != null) {
      const isSelected = selectionIds.has(focusedRowId.toString());

      handleSelectionChange({ index: focusIndex, newValue: !isSelected });
    }
  }

  return (
    <Container className={className} style={style} onKeyDown={handleKeyDown}>
      {draggable && (
        <DragLayer
          targetItemType={dragElementType}
          renderPreview={() => <Preview>{selectionIds?.size || 1} Elements</Preview>}
        />
      )}

      {withSearchBar && existsSearchableColumns && (
        <TableOptionsContainer>
          <SearchBar value={searchValue} onChange={setSearchValue} />
        </TableOptionsContainer>
      )}

      <TableContainer
        stripped={stripped}
        hover={hover}
        className="table"
        style={{
          ...tableStyle,
          overflowX: isOverflowing ? 'auto' : 'hidden',
          overflowY: 'hidden',
        }}
      >
        <AutoSizer>
          {({ height, width }) => (
            <Table
              width={isOverflowing ? usefulWidth : width}
              height={isOverflowing ? height - scrollBarWidth : height}
              headerClassName="data-table-header"
              rowClassName="data-table-row"
              sort={handleSortingChange}
              sortBy={sortBy}
              sortDirection={sortDirection}
              headerHeight={HEADER_HEIGHT}
              rowHeight={ROW_HEIGHT}
              rowStyle={({ index }) => getFinalRowStyle({ index })}
              rowCount={filteredData.length}
              rowGetter={({ index }) => filteredData[index]}
              headerStyle={defaultCellStyle}
              rowRenderer={getRowRenderer}
              onRowClick={handleRowClick}
              scrollToIndex={scrollToIndex}
              overscanRowCount={overscanRowCount}
            >
              {selectable &&
                checkboxColumn({
                  getRowSelectionState,
                  onRowSelectionChange: handleSelectionChange,
                  isSortingCheckboxCol,
                })}
              {withRowIndex && rowIndexColumn()}
              {React.Children.map(children, (child: React.ReactElement) => {
                const columnWidth =
                  child.props.width != null && child.props.width !== 0
                    ? child.props.width
                    : width / children.length;

                return React.cloneElement(child, {
                  key: child.props.id || child.props.dataKey,
                  width: columnWidth,
                });
              })}
              {columnsCanBeHidden && placeholderColumn()}
            </Table>
          )}
        </AutoSizer>
        {data.length === 0 && noDataPlaceholder}
      </TableContainer>
    </Container>
  );
};

const isEqualId = (
  a: undefined | null | string | ObjectId,
  b: undefined | null | string | ObjectId,
): boolean => {
  if (a == null || b == null) return false;
  return a.toString() === b.toString();
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  .table {
    margin-bottom: unset;
  }
`;

const TableContainer = styled.div<{ stripped: boolean; hover: boolean }>`
  border: solid 1px #dee2e6;
  border-radius: 0.25rem;
  background: white;
  height: 100%;

  .ReactVirtualized__Grid {
    outline: none;
  }

  div[role='row'] {
    :nth-of-type(2n) {
      background-color: ${({ stripped }) => (stripped ? '#f0f0f0' : 'white')};
    }

    &:not(.ReactVirtualized__Table__headerRow):hover {
      ${({ hover }) => (hover ? `background-color: ${RacemapColors.CloudBlue}` : '')};
    }
  }

  .ReactVirtualized__Table__headerRow {
    background-color: #f4f4f4;
  }
`;

const TableOptionsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 7px;

  .input-group {
    width: 400px;

    @media (max-width: ${MaxMobileWindowWidth}px) {
      width: 100%;
    }
  }
`;
